import React, { useState } from 'react';
import { Award, Globe, Lightbulb, TrendingUp, CheckCircle, Users, Factory, Target, Zap, Shield, Heart } from 'lucide-react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Navbar from '../layout/Navbar';
import Footer from '../layout/Footer';
import about_bg_img from '../assets/about_bg_img.jpg';
import group_logos from '../assets/Group_logo.jpg';
import img1 from '../assets/about_slider/img1.jpeg';
import img2 from '../assets/about_slider/img2.jpeg';
import img3 from '../assets/about_slider/img3.jpeg';
import img4 from '../assets/about_slider/img4.jpeg';
import img5 from '../assets/about_slider/img5.jpeg';
import img6 from '../assets/about_slider/img6.jpg';
import img7 from '../assets/about_slider/img7.jpeg';
import img8 from '../assets/about_slider/img8.jpeg';
import img9 from '../assets/about_slider/img9.jpeg';
import img10 from '../assets/about_slider/img10.jpeg';
import image1 from '../assets/about_slider/img6.jpg';
import image2 from '../assets/about_slider/img1.jpeg';
import image3 from '../assets/about_slider/img9.jpeg';

// Sample images for the slider
const sliderImages = [
    img1, img2, img3, img4, img5, img6, img7, img8, img9, img10
];

const About = () => {
    const [activeCompany, setActiveCompany] = useState('JANTA Ceramic Industries');

    const stats = [
        { icon: Award, number: '30+', label: 'Years of Business Experience' },
        { icon: Globe, number: '20+', label: 'Countries Served' },
        { icon: Factory, number: '700+', label: 'Projects Executed Globally' },
        { icon: TrendingUp, number: '99%', label: 'Operational Efficiency' }
    ];

    const companies = [
        'JANTA Ceramic Industries',
        'JANTA Refractories',
        'M F Refractories'
    ];

    const companyData = {
        'JANTA Ceramic Industries': {
            image: image1,
            description: 'Specialized in manufacturing premium ceramic products and advanced refractory solutions for high-temperature industrial applications.'
        },
        'JANTA Refractories': {
            image: image2,
            description: 'Leading provider of refractory bricks, castables, and mortars designed for extreme thermal environments across multiple industries.'
        },
        'M F Refractories': {
            image: image3,
            description: 'Specialized manufacturing unit focused on custom refractory solutions and innovative industrial products.'
        }
    };

    // Responsive slider settings
    const sliderSettings = {
        dots: true,
        infinite: true,
        speed: 500,
        slidesToShow: 3,
        slidesToScroll: 1,
        autoplay: true,
        autoplaySpeed: 1000,
        arrows: true,
        responsive: [
            {
                breakpoint: 1024,
                settings: {
                    slidesToShow: 2,
                    slidesToScroll: 1,
                    arrows: true,
                }
            },
            {
                breakpoint: 640,
                settings: {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    arrows: false,
                }
            }
        ]
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            {/* Hero Section */}
            <section className="relative h-[50vh] sm:h-[60vh] flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    <img
                        src={about_bg_img}
                        alt="Manufacturing Facility"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/60 to-transparent"></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-slate-900/40"></div>
                </div>
                <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 animate-fadeInUp">
                        About <span className="text-orange-500">JANTA Group</span>
                    </h1>
                    <p className="text-base sm:text-lg lg:text-xl text-gray-300 mb-6 sm:mb-8 max-w-xl sm:max-w-2xl lg:max-w-3xl mx-auto animate-fadeInUp" style={{ animationDelay: '0.2s' }}>
                        Largest Manufacturer, Supplier and Exporter of Refractory Products
                    </p>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-6 sm:py-8 bg-gradient-to-r from-yellow-500 to-orange-500">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
                        {stats.map((stat, index) => (
                            <div key={index} className="text-center transform hover:scale-105 transition-transform duration-300">
                                <stat.icon className="w-8 h-8 sm:w-10 sm:h-10 text-white mx-auto mb-3 sm:mb-4" />
                                <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-1 sm:mb-2">{stat.number}</div>
                                <div className="text-sm sm:text-base lg:text-white/90 font-medium">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section className="py-12 sm:py-16 lg:py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-start">
                        <div>
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                                Our <span className="text-orange-600">Story</span>
                            </h2>
                            <p className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-6 leading-relaxed">
                                JANTA Group of Companies are largest manufacturer, supplier and exporter of refractory products for high temperature applications. The combination of our customer-centered approach, high-performance products and broad services make up the foundation of our company's successful reputation.
                            </p>
                            <p className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-6 leading-relaxed">
                                <b>Established in 1990, our group of company includes JANTA Ceramic Industries, JANTA Refractories and M F Refractories.</b> Together we refocused within the JANTA group of Companies in 1990 while continuing to provide the same flexibility and responsiveness to our customers that made us successful in the earliest days of our family's operation.
                            </p>
                            <p className="text-base sm:text-lg text-gray-700 mb-4 sm:mb-6 leading-relaxed">
                                With the help of our Technology Centers, a large network of distribution centers, contractors and installers, our JANTA Group of Companies sales force continues to provide the best technology, cost effectiveness, outstanding quality and reliability to our valued customers.
                            </p>
                            <div className="flex items-center gap-3 text-orange-600 font-semibold text-sm sm:text-base">
                                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                                <span>30 Years of Excellence & Innovation</span>
                            </div>
                        </div>
                        <div className="relative h-full flex flex-col justify-center">
                            <div className="space-y-4 sm:space-y-6">
                                <div className="w-full">
                                    <img
                                        src={group_logos}
                                        alt="Refractory Manufacturing"
                                        className="rounded-2xl w-full h-[200px] sm:h-[250px] lg:h-[300px] object-contain bg-white shadow-lg"
                                    />
                                </div>
                                <div className="w-full">
                                    <Slider {...sliderSettings}>
                                        {sliderImages.map((image, index) => (
                                            <div key={index} className="px-1 sm:px-2">
                                                <img
                                                    src={image}
                                                    alt={`Slide ${index + 1}`}
                                                    className="w-full h-[150px] sm:h-[200px] lg:h-[250px] object-contain rounded-lg shadow-md"
                                                />
                                            </div>
                                        ))}
                                    </Slider>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Group Companies Section */}
            <section className="py-12 sm:py-16 lg:py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-start">
                        <div className="order-2 md:order-1 relative">
                            <div className="relative overflow-hidden rounded-2xl shadow-2xl h-[250px] sm:h-[300px] lg:h-[400px]">
                                {Object.entries(companyData).map(([company, data]) => (
                                    <img
                                        key={company}
                                        src={data.image}
                                        alt={company}
                                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${activeCompany === company ? 'opacity-100' : 'opacity-0'}`}
                                    />
                                ))}
                            </div>
                            <div className="absolute -bottom-4 -right-4 sm:-bottom-6 sm:-right-6 bg-gradient-to-r from-yellow-500 to-orange-500 text-white p-4 sm:p-6 rounded-xl shadow-xl">
                                <div className="text-lg sm:text-xl lg:text-2xl font-bold">Group of 3</div>
                                <div className="text-xs sm:text-sm">Leading Companies</div>
                            </div>
                        </div>
                        <div className="order-1 md:order-2">
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">
                                Our <span className="text-orange-600">Companies</span>
                            </h2>
                            <div className="mb-6 sm:mb-8 min-h-[80px] sm:min-h-[100px]">
                                <p className="text-base sm:text-lg text-gray-700 leading-relaxed transition-all duration-500">
                                    {companyData[activeCompany].description}
                                </p>
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:gap-4">
                                {companies.map((company, index) => (
                                    <div
                                        key={index}
                                        onMouseEnter={() => setActiveCompany(company)}
                                        onClick={() => setActiveCompany(company)}
                                        className={`flex items-center gap-3 p-3 sm:p-4 rounded-lg cursor-pointer transition-all duration-300 ${activeCompany === company
                                            ? 'bg-orange-500 text-white shadow-lg scale-105'
                                            : 'bg-gray-50 hover:bg-orange-50 text-gray-800'
                                            }`}
                                    >
                                        <Factory className={`w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0 transition-colors ${activeCompany === company ? 'text-white' : 'text-orange-600'}`} />
                                        <span className="font-medium text-sm sm:text-base">{company}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Key Highlights Section */}
            <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8 sm:mb-12 lg:mb-16">
                        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">Why Choose <span className="text-orange-500">JANTA Group</span></h2>
                        <p className="text-base sm:text-lg lg:text-xl text-gray-300">Built on Excellence, Innovation & Trust</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 sm:p-8 hover:bg-white/15 transition-all">
                            <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mb-6">
                                <Users className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                            </div>
                            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-3 sm:mb-4">In-House R&D Team</h3>
                            <p className="text-sm sm:text-base text-gray-300 leading-relaxed">
                                Dedicated research and development team for products and applications, ensuring continuous innovation and quality improvement.
                            </p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 sm:p-8 hover:bg-white/15 transition-all">
                            <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mb-6">
                                <Zap className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                            </div>
                            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-3 sm:mb-4">Kaizen Philosophy</h3>
                            <p className="text-sm sm:text-base text-gray-300 leading-relaxed">
                                High operational efficiency with Kaizen philosophy, ensuring continuous improvement and optimization in all processes.
                            </p>
                        </div>
                        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 sm:p-8 hover:bg-white/15 transition-all">
                            <div className="w-16 h-16 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mb-6">
                                <Globe className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                            </div>
                            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold mb-3 sm:mb-4">Global Presence</h3>
                            <p className="text-sm sm:text-base text-gray-300 leading-relaxed">
                                Over 700+ projects executed globally with customers in 20+ countries, backed by 30 years of business experience.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            <style jsx>{`
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-fadeInUp {
                    animation: fadeInUp 0.8s ease-out forwards;
                    opacity: 0;
                }
                /* Custom slick slider styles for responsiveness */
                .slick-prev, .slick-next {
                    z-index: 10;
                    width: 30px;
                    height: 30px;
                    background: rgba(0, 0, 0, 0.5);
                    border-radius: 50%;
                }
                .slick-prev {
                    left: 10px;
                }
                .slick-next {
                    right: 10px;
                }
                .slick-dots {
                    bottom: -30px;
                }
                .slick-dots li button:before {
                    font-size: 10px;
                    color: #fff;
                }
                .slick-dots li.slick-active button:before {
                    color: #f97316;
                }
                @media (max-width: 640px) {
                    .slick-prev, .slick-next {
                        display: none !important;
                    }
                    .slick-dots {
                        bottom: -20px;
                    }
                }
            `}</style>
            <Footer />
        </div>
    );
};

export default About;